import * as Sentry from '@sentry/browser';
import axios from 'axios';
import { newEvent } from '@utils/newEvent';
import { renderPlaceholderLoadMore } from '@componentsIsport';
import { isPayingUser } from '@utils/user';
import {
  EVENT_BEFORE_LOAD,
  EVENT_AFTER_LOAD,
  EVENT_AFTER_INSERT,
  EVENT_ERROR,
  DEFAULT_CONFIG,
  LoadMoreConfig,
} from './ConfigLoadMore';

export class ClassLoadMore {
  element: HTMLLinkElement;

  elementContent: string;

  textElement: HTMLSpanElement;

  isFetching: boolean;

  target: HTMLDivElement;

  config: LoadMoreConfig;

  page?: number;

  insert?: object;

  constructor(element: HTMLLinkElement, target: HTMLDivElement, config: LoadMoreConfig) {
    this.element = element;
    this.textElement = this.element.querySelector('.button__text') as HTMLSpanElement;
    this.target = target;
    this.config = this.getConfig(config) as LoadMoreConfig;
    this.page = this.config.page;

    // Existuje boxId?
    if (this.config.boxId === null || !this.target) {
      throw Error('Error with "boxId" or target element.');
    }

    this.elementContent = this.element.innerHTML;

    this.isFetching = false;

    this.insert = {
      before: (elements: Element[]) => {
        elements.forEach((el: Element) => {
          if (this.target != null) {
            this.target.insertAdjacentElement('beforebegin', el);
          }
        });
      },
      after: (elements: Element[]) => {
        elements.forEach((el: Element) => {
          this.target.insertAdjacentElement('afterend', el);
        });
      },
      inside: (elements: Element[]) => {
        elements.forEach((el) => this.target.appendChild(el));
      },
    };

    this.addListeners();
  }

  getConfig(config: LoadMoreConfig) {
    return { ...DEFAULT_CONFIG, ...config };
  }

  // Před načtením dat
  beforeLoad(): void {
    if (typeof this.config.loadingText !== 'undefined') {
      this.textElement.textContent = this.config.loadingText;
    }
    this.isFetching = true;

    if (typeof this.page !== 'undefined' && this.page !== null) {
      this.element.dispatchEvent(
        newEvent(EVENT_BEFORE_LOAD, { detail: { pageNumber: this.page } })
      );
    }
  }

  // // Po načtení dat
  afterLoad(): void {
    this.element.innerHTML = this.elementContent;
    if (typeof this.page !== 'undefined') {
      this.page += 1;
    }
    this.isFetching = false;
    this.element.dispatchEvent(newEvent(EVENT_AFTER_LOAD));
  }

  // Po vložení do DOMu
  afterInsert(): void {
    this.element.dispatchEvent(newEvent(EVENT_AFTER_INSERT));
  }

  // Načtení dat
  async loadData(): Promise<Array<Element>> {
    const { boxId }: LoadMoreConfig = this.config;
    try {
      /* eslint-disable */
      type Response = {
        [key: string]: any;
      };
      /* eslint-enable */
      // TODO: prozkoumat aby když se jednou klikne na tlačítko nešlo kliknout znovu a nerozbilo to načítání članku co posila backend id recombee
      // const disabledElement = document.querySelector('[data-recombee-disabled]');
      // if (disabledElement) {
      //   // Pokud se už kliklo na načíst další vykoná se pouze jednou
      //   return [];
      // }

      // Získání všech elementů s atributem 'data-article-id' abychom je vyloučili z načtení dalších článků pro potlačení duplicit
      const articleIdElements = document.querySelectorAll('[data-article-id]');
      const articleIdsToExclude: Array<string | null> = Array.from(articleIdElements).map(
        (templateArticleId) => templateArticleId.getAttribute('data-article-id')
      );

      // Převod pole id článků na řetězec do Ajax volání
      const articleIdsInStringToExclude = articleIdsToExclude.join(',');
      const getParamsToCall = `?_sobfr=${boxId as string}&ajaxload`;

      const response: Response = await axios.postForm(getParamsToCall, {
        exclude_articles: articleIdsInStringToExclude,
      });
      const parser = new DOMParser();

      return [...parser.parseFromString(response.data as string, 'text/html').body.children];
    } catch (err) {
      Sentry.captureMessage(err as string);

      this.element.dispatchEvent(newEvent(EVENT_ERROR));
      return [];
    }
  }

  // Umístí elementy dle definovaného cíle
  insertElements(elements: Element[]) {
    const { insertPlace } = this.config;

    if (!this.insert || typeof insertPlace === 'undefined') return;

    /* eslint-disable */
    (this.insert as any)[insertPlace](elements);
    /* eslint-enable */
  }

  transformElements(elements: Element[]): Element[] {
    const { transform } = this.config;

    return typeof transform === 'function' ? (transform(elements) as Element[]) : elements;
  }

  // Nahradit tlačítko pokud neexistují další články, nebo se dosáhne limitu stránek (30)
  replaceButtonWithPlaceholder(): void {
    if (this.config.magazineName) {
      const placeholder = renderPlaceholderLoadMore(this.config.magazineName);
      if (placeholder) {
        this.target.insertAdjacentElement('afterend', placeholder);
        if (this.element != null) {
          this.element.remove();
        }
      }
    }
  }

  async clickHandler(event: Event): Promise<void> {
    event.preventDefault();

    this.beforeLoad();

    const elements = await this.loadData();

    if (elements.length === 0 || this.config.paginationLimit === this.page) {
      this.replaceButtonWithPlaceholder();
      return;
    }

    // Vytvoření adElementu pro vykreslení reklamy pro neplatící uživatele
    if (!isPayingUser()) {
      const adElement = document.createElement('div');
      adElement.className = 'cnc_repeater_ad_position';

      if (elements.length >= 2) {
        // Vložení reklamy do pole elements na 3. pozici (po 2. článku)
        elements.splice(2, 0, adElement);

        // Přidání reklamy do fronty pro lazy loading
        window.__cncAdsQueue = window.__cncAdsQueue || [];
        window.__cncAdsQueue.push(() => {
          adElement.insertAdjacentHTML(
            'beforebegin',
            '<!-- pozice repeater vlozena javascriptem / rectangle_repeater / lazyload-->'
          );
          window.__cncLazyLoad(adElement);
        });
      }
    }

    this.afterLoad();

    const transformedElems: Element[] = this.transformElements(elements);
    this.insertElements(transformedElems);

    this.afterInsert();
  }

  addListeners() {
    this.element.addEventListener('click', this.clickHandler.bind(this));
  }
}
